import React, { useState, Suspense, lazy } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import get from 'lodash/fp/get'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { MyEvent } from '../../../types'
import { SimpleLoader, SmallLoader } from '../../../components'
import { GET_EVENT_SETTINGS } from '../../../graphql'
import { Common404 } from '../../../components/Error404'
import { CustomTabs } from '../../../styles/commonStyles'

import { PostSMSPreview } from './components/PostSMSPreview'
import { SettingsCard, RSVPSettingsPreview } from './components'

const BannersModule = React.lazy(
  () => import('../../../components/BannersModule/BannersModule')
)

const InvitationTemplatePreview = lazy(
  () => import('./components/InvitationTemplatePreview')
)

export const Settings: React.FC = () => {
  const [version, setVersion] = useState('desktop')
  const [previewHelper, setPreviewHelper] = useState(0)
  const [activeTab, setActiveTab] = useState(0)

  const { eventId } = useParams<{ eventId: string }>()
  const variables = { id: eventId }
  const { data, loading } = useQuery(GET_EVENT_SETTINGS, { variables })
  const event: MyEvent = get('me.myEventInfo', data)

  const tabs = [
    { index: 0, label: 'Post SMS Preview' },
    { index: 1, label: 'Invitation Template' },
  ]

  const handleTabChange = (index: number) => {
    setActiveTab(index)
  }

  if (loading) return <SimpleLoader />
  if (!event) return <Common404 text="Data not available" />

  return (
    <Container fluid key={`event-settings-${eventId}`}>
      <Helmet>
        <title>Kiss & Tell - Event Settings</title>
        <meta
          name="description"
          content="Customize your event settings and preview the changes in real time."
        />
      </Helmet>
      <Row>
        <Col sm={12} lg={5} className="setting-card-helper">
          <SettingsCard
            event={event}
            setPreviewHelper={setPreviewHelper}
            previewHelper={previewHelper}
          />
        </Col>
        <Col sm={12} lg={7}>
          <RSVPSettingsPreview
            key={`preview-${previewHelper}`}
            eventId={eventId}
            version={version}
            setVersion={setVersion}
            event={event}
            setPreviewHelper={setPreviewHelper}
            previewHelper={previewHelper}
          />
          <CustomTabs className="mb-3 position-relative" style={{ zIndex: 1 }}>
            {tabs.map(tab => (
              <button
                key={`settings-tab-${tab.index}`}
                type="button"
                onClick={() => handleTabChange(tab.index)}
                className={activeTab === tab.index ? 'active' : ''}
              >
                {tab.label}
              </button>
            ))}
          </CustomTabs>

          {activeTab === 0 && (
            <PostSMSPreview
              key={`post-sms-preview-${previewHelper}`}
              event={event}
            />
          )}

          {activeTab === 1 && (
            <Suspense fallback={<SmallLoader />}>
              <InvitationTemplatePreview
                key={`email-preview-${previewHelper}`}
                event={event}
              />
            </Suspense>
          )}
        </Col>
      </Row>

      <Container as="section" className="mt-5">
        <h3 className="pb-2 fwl fz-35 text-center">
          Select your date by booking a venue or group stay!
        </h3>
        <Suspense fallback={<SimpleLoader />}>
          {/** In this view the user will always have events, so it's not necessary to use a query */}
          <BannersModule myEvents={1} text="Search Venues" />
        </Suspense>
      </Container>
    </Container>
  )
}
